// Every case across the years — the archive's shelf: filter by year, status,
// retention class or words; open one for its records.

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { records } from '../lib/api.js';
import { fmtDay } from '../lib/journals.js';
import { periodLabel, titleIn } from '../lib/retention.js';
import { CaseSheet } from './CaseSheet.jsx';

const ALL = 'all';

export function CasesView({ access, classes, selectedCaseUuid, onSelectCase }) {
  const { t, i18n } = useTranslation('records');
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [classUuid, setClassUuid] = useState(ALL);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    const res = await records.cases({
      year: year === ALL ? undefined : Number(year),
      status: status === ALL ? undefined : status,
      class_uuid: classUuid === ALL ? undefined : classUuid,
      q: q.trim() || undefined,
    });
    if (res.success) setRows(res.response);
    else toast.error(res.message);
  }, [year, status, classUuid, q]);

  useEffect(() => {
    const timer = setTimeout(load, 200);
    return () => clearTimeout(timer);
  }, [load]);

  const years = Array.from({ length: 8 }, (_, i) => String(thisYear + 1 - i));

  return (
    <div className="space-y-3" data-testid="cases-view">
      <div className="flex flex-wrap items-center gap-2">
        <Input className="h-9 w-56" maxLength={200} placeholder={t('cases.search')} aria-label={t('cases.search')} value={q} onChange={(e) => setQ(e.target.value)} />
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="h-9 w-28" aria-label={t('entry.year')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('entry.allYears')}</SelectItem>
            {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-36" aria-label={t('entry.status')}><SelectValue /></SelectTrigger>
          <SelectContent>
            {[ALL, 'open', 'closed', 'in_review', 'transferred', 'destroyed'].map((s) => <SelectItem key={s} value={s}>{t(`cases.statuses.${s}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={classUuid} onValueChange={setClassUuid}>
          <SelectTrigger className="h-9 w-64" aria-label={t('case.retention')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('cases.allClasses')}</SelectItem>
            {classes.map((c) => (
              <SelectItem key={c.uuid} value={c.uuid}>{c.article} · {titleIn(c.title, i18n.language)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!rows ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="cases-empty">{t('cases.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2">{t('plan.year')}</th>
                <th className="px-3 py-2">{t('case.index')}</th>
                <th className="px-3 py-2">{t('case.title')}</th>
                <th className="px-3 py-2">{t('case.retention')}</th>
                <th className="px-3 py-2">{t('cases.records')}</th>
                <th className="px-3 py-2">{t('case.keptUntil')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.uuid} className="cursor-pointer border-t hover:bg-muted/40" onClick={() => onSelectCase(c.uuid)} data-testid={`cases-row-${c.case_index}`}>
                  <td className="px-3 py-2 text-xs">{c.year}</td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{c.case_index}</td>
                  <td className="px-3 py-2">
                    {c.title}
                    {c.unit && <span className="block text-xs text-muted-foreground">{c.unit}</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs">{periodLabel(c.retention, t)}</td>
                  <td className="px-3 py-2 tabular-nums">{c.items}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs">
                    {c.status === 'closed'
                      ? (c.retention_until ? fmtDay(c.retention_until) : t('case.noEnd'))
                      : <Badge variant="outline">{t(`case.statuses.${c.status}`)}</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CaseSheet
        caseUuid={selectedCaseUuid}
        canManage={access.can_manage}
        canReview={access.can_review}
        onOpenChange={(open) => { if (!open) onSelectCase(null); }}
        onChanged={load}
      />
    </div>
  );
}
